(function () {
  const SECTION_CLASS = "key-metrics-section";
  const ROOT_SELECTOR = ".key-metrics";
  const METRICS_SELECTOR = "[data-key-metrics-animate]";
  const HEADING_SELECTOR = ".key-metrics__item .block-heading";
  const DIGITS = "0123456789";
  const DURATION = 1400;
  const STAGGER = 90;
  // Fixed-point denominator for the count-up easing, which BigInt cannot take as a float.
  const EASE_SCALE = 1000000;
  // Digit strips run two full 0-9 cycles before landing, so every column rolls
  // upwards for the same visible distance whatever its final digit is.
  const LOOPS = 2;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  class KeyMetrics {
    constructor(root) {
      this.root = root;
      this.entries = [];
      this.observer = null;
      this.resizeObserver = null;
      this.raf = null;
      this.destroyed = false;

      const metrics = root.querySelector(METRICS_SELECTOR);
      if (!metrics || reduceMotion.matches) return;

      this.odometer = metrics.hasAttribute("data-key-metrics-odometer");

      // Every digit slot is pinned to a measured glyph width, so the measuring has
      // to wait for the real face — taken against the fallback the slots come out
      // the wrong size and the rolling layer sits off the value it lands on.
      const fonts = document.fonts;
      if (fonts && fonts.status !== "loaded") {
        fonts.ready.then(() => this.build(metrics));
      } else {
        this.build(metrics);
      }
    }

    build(metrics) {
      if (this.destroyed) return;

      Array.from(metrics.querySelectorAll(HEADING_SELECTOR)).forEach(
        (heading) => {
          const entry = this.prepare(heading);
          if (entry) this.entries.push(entry);
        }
      );

      if (!this.entries.length) return;

      this.observer = new IntersectionObserver(
        (observed) => {
          observed.forEach((item) => {
            if (!item.isIntersecting) return;

            this.observer.unobserve(item.target);
            this.play(item.target);
          });
        },
        { threshold: 0.4 }
      );

      this.entries.forEach((entry) => this.observer.observe(entry.heading));

      // Every slot is pinned in px, taken from the heading's own type — a viewport
      // step that resizes that type leaves the digits standing apart, with the last
      // of them over the copy beside the number. Re-measure when the band resizes.
      if (typeof ResizeObserver === "function") {
        this.resizeObserver = new ResizeObserver(() =>
          this.scheduleRemeasure()
        );
        this.resizeObserver.observe(metrics);
      }
    }

    scheduleRemeasure() {
      if (this.raf) window.cancelAnimationFrame(this.raf);

      this.raf = window.requestAnimationFrame(() => {
        this.raf = null;
        this.remeasure();
      });
    }

    remeasure() {
      if (this.destroyed) return;

      this.entries.forEach((entry) => this.resizeEntry(entry));
    }

    // Re-sizes one heading's stand-ins against the type it has been left with.
    resizeEntry(entry) {
      const digitHeight = this.measureDigitHeight(entry.heading);

      entry.numbers.forEach((number) => {
        number.wrappers.forEach((wrapper) =>
          wrapper.style.setProperty("--km-digit-height", digitHeight + "px")
        );
        this.applyDigitWidths(number);

        // A strip already on its way lands on the cell height the new type gives it.
        number.columns.forEach((column) => {
          if (column.rolled) this.rollColumn(column);
        });
      });
    }

    // Swaps every digit run in the heading for its animated stand-in, left at zero.
    // Returns null when the heading holds no digits, so it stays untouched.
    prepare(heading) {
      const textNodes = [];
      const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) textNodes.push(walker.currentNode);

      const targets = textNodes.filter((node) => /\d/.test(node.nodeValue));
      if (!targets.length) return null;

      const entry = {
        heading,
        html: heading.innerHTML,
        numbers: [],
        frames: [],
        timers: [],
      };
      const digitHeight = this.measureDigitHeight(heading);

      targets.forEach((node) => {
        const fragment = document.createDocumentFragment();

        node.nodeValue.split(/(\d+)/).forEach((part) => {
          if (!part) return;

          if (!/^\d+$/.test(part)) {
            fragment.appendChild(document.createTextNode(part));
            return;
          }

          fragment.appendChild(this.buildNumber(part, digitHeight, entry));
        });

        node.parentNode.replaceChild(fragment, node);
      });

      // Now that the stand-ins are in the document, pin every digit slot to the width
      // its FINAL digit occupies. The type is proportional, so a rolling "0" is wider
      // than the "1" it lands on: without this the animated layer runs wider than the
      // value, overlapping the copy next to it and snapping back once it settles.
      entry.numbers.forEach((number) => this.applyDigitWidths(number));

      return entry;
    }

    // Every slot reserves the WIDEST digit of the face, not the width of the digit it
    // lands on. The figures are proportional (this theme has no tabular ones), so a slot
    // cut to a final "1" left every digit rolling through it visibly squeezed. Reserved
    // this way the roll needs no scaling; the value settles back into plain text, which
    // gives the reserved space back.
    applyDigitWidths(number) {
      if (!number.ghosts.length) return;

      const glyphs = this.measureGlyphs(number.ghosts[0]);
      if (!glyphs) return;

      const width = Math.max.apply(
        null,
        DIGITS.split("").map((digit) => glyphs[digit])
      );
      if (!width) return;

      number.wrappers.forEach((wrapper) =>
        wrapper.style.setProperty("--km-digit-width", width + "px")
      );
    }

    // Glyph widths for 0-9 in the heading's own type, measured inside the ghost so the
    // sample carries the heading's own font, size and weight.
    measureGlyphs(sample) {
      if (!sample) return null;

      const probe = document.createElement("span");
      probe.setAttribute("aria-hidden", "true");
      probe.style.cssText =
        "position:absolute;visibility:hidden;white-space:pre;";
      sample.appendChild(probe);

      const glyphs = {};
      DIGITS.split("").forEach((digit) => {
        probe.textContent = digit;
        glyphs[digit] = probe.getBoundingClientRect().width;
      });

      probe.remove();
      return glyphs;
    }

    measureDigitHeight(heading) {
      const styles = window.getComputedStyle(heading);
      const lineHeight = parseFloat(styles.lineHeight);
      if (Number.isFinite(lineHeight)) return lineHeight;

      const fontSize = parseFloat(styles.fontSize);
      return Number.isFinite(fontSize) ? fontSize * 1.2 : 0;
    }

    // One stand-in per DIGIT rather than one per number: each is an atomic inline-block,
    // so a number built as a single box could not wrap and a long value either ran over
    // the metric beside it or had to give up its animation. Split per digit, the line
    // breaks between them the way plain text does and every digit still rolls.
    buildNumber(raw, digitHeight, entry) {
      // The digits share one inline group: each stand-in is an object to the bidi
      // algorithm, so in an RTL paragraph (an Arabic caption beside the figure) they
      // were ordered right to left and the number read backwards. The group isolates
      // them as one LTR run and, being inline, still breaks across lines.
      const fragment = document.createElement("span");
      fragment.className = "key-metrics__number-group";
      const number = {
        raw,
        label: null,
        wrappers: [],
        ghosts: [],
        visuals: [],
        columns: [],
        cells: [],
      };

      // The real value stays in the accessibility tree the whole time, so the
      // rolling stand-ins never have to be read out.
      const label = document.createElement("span");
      label.className = "visually-hidden";
      label.textContent = raw;
      number.label = label;
      fragment.appendChild(label);

      raw.split("").forEach((digit, index) => {
        const wrapper = document.createElement("span");
        wrapper.className = "key-metrics__number";
        wrapper.setAttribute("aria-hidden", "true");
        wrapper.style.setProperty("--km-digit-height", digitHeight + "px");
        wrapper.style.setProperty("--km-odometer-duration", DURATION + "ms");

        // The final digit stays in flow but invisible: it holds the width and the
        // baseline, so the animated layer can be lifted out of flow on top of it.
        const ghost = document.createElement("span");
        ghost.className = "key-metrics__number-ghost";
        ghost.textContent = digit;
        wrapper.appendChild(ghost);

        const visual = document.createElement("span");
        visual.className = "key-metrics__number-value";
        wrapper.appendChild(visual);

        if (this.odometer) {
          visual.classList.add("key-metrics__odometer");

          const column = document.createElement("span");
          column.className = "key-metrics__odometer-column";
          column.style.setProperty(
            "--km-odometer-delay",
            index * STAGGER + "ms"
          );

          const strip =
            DIGITS.repeat(LOOPS) + DIGITS.slice(0, Number(digit) + 1);
          strip.split("").forEach((cell) => {
            const cellEl = document.createElement("span");
            cellEl.className = "key-metrics__odometer-cell";
            cellEl.textContent = cell;
            column.appendChild(cellEl);
          });

          number.columns.push({ el: column, rolled: false });
          visual.appendChild(column);
        } else {
          const cell = document.createElement("span");
          cell.className = "key-metrics__counter-digit";
          cell.textContent = "0";
          number.cells.push(cell);
          visual.appendChild(cell);
        }

        number.wrappers.push(wrapper);
        number.ghosts.push(ghost);
        number.visuals.push(visual);
        fragment.appendChild(wrapper);
      });

      entry.numbers.push(number);
      return fragment;
    }

    play(heading) {
      const entry = this.entries.find((item) => item.heading === heading);
      if (!entry) return;

      entry.numbers.forEach((number) => {
        if (this.odometer) {
          this.rollDigits(entry, number);
        } else {
          this.countUp(entry, number);
          this.scheduleSettle(entry, number, () => DURATION + 60);
        }
      });
    }

    rollDigits(entry, number) {
      number.columns.forEach((column) => this.rollColumn(column));

      this.scheduleSettle(
        entry,
        number,
        (index) => DURATION + index * STAGGER + 60
      );
    }

    // Landing is measured off the strip itself rather than counted as cells × the line
    // height JS read: layout rounds a cell to its own subpixel grid, and over the ~25
    // cells of a strip that difference piles up until the digit sits off its own value —
    // which then snapped into place at the swap (Safari rounds coarsest, so it showed
    // there first). The gap between two cells moves with the column, so this also
    // re-measures correctly mid-roll.
    rollColumn(column) {
      const cells = column.el.children;
      const first = cells[0];
      const last = cells[cells.length - 1];
      if (!first || !last) return;

      const distance =
        last.getBoundingClientRect().top - first.getBoundingClientRect().top;

      column.rolled = true;
      column.el.style.transform = "translateY(-" + distance + "px)";
    }

    // A landed digit drops its rolling layer and hands the slot back to the ghost it was
    // covering — one digit in the markup again, so the value reads and copies as itself.
    // The slot WIDTH stays: the widths are what the line breaks are computed from, and
    // giving them back threw a digit of a wrapped number up onto the line above, shifting
    // the whole row by a slot.
    scheduleSettle(entry, number, delayFor) {
      number.ghosts.forEach((ghost, index) => {
        entry.timers.push(
          window.setTimeout(() => {
            number.visuals[index].remove();
            ghost.classList.remove("key-metrics__number-ghost");
            number.wrappers[index].removeAttribute("aria-hidden");

            // The value is back in the document, so the stand-in label would read twice.
            if (index === number.ghosts.length - 1) number.label.remove();
          }, delayFor(index))
        );
      });
    }

    // Counted in BigInt against the raw string: as a Number a long run loses its tail to
    // floating point and, past ~21 digits, prints as "1.23e+28" — those characters went
    // straight into the digit cells.
    countUp(entry, number) {
      const target = BigInt(number.raw);
      const pad = number.raw.length;
      const start = performance.now();

      const step = (now) => {
        const progress = Math.min((now - start) / DURATION, 1);
        const eased = 1 - Math.pow(1 - progress, 3);

        const value = (
          progress < 1
            ? (
                (target * BigInt(Math.round(eased * EASE_SCALE))) /
                BigInt(EASE_SCALE)
              ).toString()
            : number.raw
        ).padStart(pad, "0");

        number.cells.forEach((cell, index) => {
          cell.textContent = value.charAt(index);
        });

        if (progress < 1) entry.frames.push(window.requestAnimationFrame(step));
      };

      entry.frames.push(window.requestAnimationFrame(step));
    }

    destroy() {
      this.destroyed = true;

      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.raf) {
        window.cancelAnimationFrame(this.raf);
        this.raf = null;
      }

      this.entries.forEach((entry) => {
        entry.frames.forEach((frame) => window.cancelAnimationFrame(frame));
        entry.timers.forEach((timer) => window.clearTimeout(timer));
        entry.heading.innerHTML = entry.html;
      });

      this.entries = [];
    }
  }

  function initSection(section) {
    if (
      !section ||
      !section.classList ||
      !section.classList.contains(SECTION_CLASS)
    )
      return;

    const root = section.querySelector(ROOT_SELECTOR);
    if (!root) return;

    if (root._keyMetricsInstance) {
      root._keyMetricsInstance.destroy();
      root._keyMetricsInstance = null;
    }

    root._keyMetricsInstance = new KeyMetrics(root);
  }

  if (document.currentScript) initSection(document.currentScript.parentElement);

  if (!window.__themeKeyMetricsEvents) {
    window.__themeKeyMetricsEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      event.target.querySelectorAll(ROOT_SELECTOR).forEach((el) => {
        if (!el._keyMetricsInstance) return;

        el._keyMetricsInstance.destroy();
        el._keyMetricsInstance = null;
      });
    });
  }
})();
